/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import _ from 'lodash';
import Konva from 'konva';

// ours
import TLBar, { TLBarType } from '../elements/TLBar';
import TLBarColumn from '../elements/TLBarColumn';
import { JaegerEvent } from '../data/JaegerEvent.enum';
import NavigationService from '../../../services/navigation.service';
import { CashFlowModel } from 'src/types/cash-flow.model';
import CalcFactory from 'src/bw/factories/calc.factory';
import UnitService from '../../../services/unit.service';
import { JaegerService } from '../../../services/jaeger.service';

export default class BarFactory {
    private static instance: BarFactory;

    // prevent new with private constructor
    private constructor() {}

    public static getInstance(): BarFactory {
        if (!BarFactory.instance) {
            BarFactory.instance = new BarFactory();
        }

        return BarFactory.instance;
    }

    public setParams(ctx: TLBar, xPosition: number, yPosition: number): void {
        ctx.xPosition = xPosition;
        ctx.yPosition = yPosition;
        // TODO: maybe need more params to get passed
    }

    public build(ctx: TLBar): void {
        this.upsertBarGroup(ctx);
        this.setupEmptyRectangle(ctx);
        this.setupFilledRect(ctx);
        this.addListeners(ctx);
    }

    private upsertBarGroup(ctx: TLBar): void {
        if (ctx.barGroup) {
            this.configureBar(ctx);
            ctx.barGroup.x(ctx.xPosition);
            ctx.barGroup.y(ctx.yPosition);
            ctx.barGroup.width(ctx.width);
            ctx.barGroup.height(ctx.maxHeight);
            ctx.barGroup.attrs.index = ctx.parent.index;
            ctx.barGroup.attrs.date = ctx.parent.startDate;
            ctx.barGroup.attrs.type = ctx.type;
        } else {
            ctx.barGroup = new Konva.Group({
                x: ctx.xPosition,
                y: ctx.yPosition,
                width: ctx.width,
                height: ctx.maxHeight,
                index: ctx.parent.index,
                date: ctx.parent.startDate,
                type: ctx.type,
                perfectDrawEnabled: false
            });
        }
    }

    public configureBar(ctx: TLBar, parentArg?: TLBarColumn, barTypeArg?: TLBarType): void {
        ctx.parent = parentArg ?? ctx.parent;
        ctx.projectJaeger = ctx.parent.parent.parent;
        ctx.barType = barTypeArg ?? ctx.barType;
        ctx.xPosition = ctx.parent.xPosition;
        ctx.maxHeight = ctx.parent.parent.height / 2 - 4;
        ctx.width = ctx.parent.parent.barWidth;

        if (ctx.barType === TLBarType.TOP) {
            ctx.yPosition = 4;
            ctx.type = ctx.parent.parent.topBarType;
            ctx.url = ctx.parent.topBarUrl;
            ctx.height = ctx.parent.topBarHeight;
        } else {
            ctx.yPosition = ctx.maxHeight + 4;
            ctx.type = ctx.parent.parent.bottomBarType;
            ctx.url = ctx.parent.bottomBarUrl;
            ctx.height = ctx.parent.bottomBarHeight;
        }
    }

    /**
     * // without this invisible rect the mouse over is just the height of the top bar
     * @private
     */
    private setupEmptyRectangle(ctx: TLBar): void {
        if (ctx.emptyRect) {
            ctx.emptyRect.width(ctx.width);
            ctx.emptyRect.height(ctx.maxHeight);
        } else {
            ctx.emptyRect = new Konva.Rect({
                x: 0,
                y: 0,
                width: ctx.width,
                height: ctx.maxHeight,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            ctx.barGroup.add(ctx.emptyRect);
        }
    }

    private setupFilledRect(ctx: TLBar): void {
        let yPosition: number;
        let height: number;
        if (ctx.barType === TLBarType.TOP) {
            yPosition = ctx.maxHeight - 4;
            height = ctx.maxHeight * -ctx.height;
        } else {
            yPosition = 0;
            height = ctx.maxHeight * ctx.height;
        }

        if (_.isNaN(height)) height = 0;

        if (ctx.barRect) {
            ctx.barRect.y(yPosition);
            ctx.barRect.width(ctx.width);
            ctx.barRect.height(height);
            ctx.barRect.setAttr('fill', ctx.fill);
        } else {
            ctx.barRect = new Konva.Rect({
                x: 0,
                y: yPosition,
                width: ctx.width,
                height,
                perfectDrawEnabled: false,
                fill: ctx.fill,
                shadowForStrokeEnabled: false
            });

            ctx.barGroup.add(ctx.barRect);
        }

        (window as any)[`${ctx.key}-height`] = height;
    }

    public onClick(ctx: TLBar): void {
        NavigationService.goTo(ctx.projectJaeger.props.history, ctx.url, `${ctx.type} drawer`).catch((e: any) =>
            console.error(e)
        );
    }

    public onMouseEnter(ctx: TLBar): void {
        try {
            const jaegerCashFlow = ctx.timelineService.getCashFlow();
            const top: boolean = ctx.barType === TLBarType.TOP;
            const cf: CashFlowModel[] =
                ctx.type && ctx.type === 'netWorth'
                    ? jaegerCashFlow.map((cfEntry: CashFlowModel) => {
                          cfEntry.netWorth = CalcFactory.getNetWorth(cfEntry);
                          return cfEntry;
                      })
                    : jaegerCashFlow;

            const value: number = Number(_.get(cf[ctx.parent.index], ctx.type));
            if (!_.isNumber(value)) return;
            if (ctx.type === 'netWorth') {
                ctx.value = value;
            } else {
                ctx.value = Math.abs(value);
            }

            const barX: number = UnitService.getJaegerX(
                ctx.projectJaeger.props.context,
                ctx.projectJaeger.props.user,
                ctx.parent.startDate
            );
            const barWidth: number = JaegerService.getBarWidth(
                ctx.projectJaeger.props.context,
                ctx.projectJaeger.props.user,
                jaegerCashFlow
            );

            ctx.projectJaeger.props.showWork(
                jaegerCashFlow[ctx.parent.index],
                ctx.type,
                barX + barWidth / 2 + ctx.projectJaeger.stage.x(),
                top ? JaegerService.getJaegerYTopBar() : JaegerService.getJaegerYBottomBar(),
                ctx.value,
                ctx.projectJaeger.state.zoom
            );

            ctx.projectJaeger.highlightGroup.show(barWidth, barX);
        } catch (error) {
            console.error(error);
        }
    }

    public onMouseExit(ctx: TLBar): void {
        try {
            ctx.projectJaeger.props.endShowWork(ctx.parent.index, ctx.type);
            ctx.projectJaeger.setState({
                currentBar: null
            });
        } catch (error) {
            console.error(error);
        }

        ctx.projectJaeger.highlightGroup.hide();
    }

    private addListeners(ctx: TLBar): void {
        this.setClickListener(ctx);
        this.setMouseEnterListener(ctx);
        this.setMouseLeaveListener(ctx);
    }

    private setClickListener(ctx: TLBar): void {
        ctx.barGroup.off(JaegerEvent.CLICK);
        ctx.barGroup.on(JaegerEvent.CLICK, () => {
            this.onClick(ctx);
        });
    }

    private setMouseEnterListener(ctx: TLBar): void {
        ctx.barGroup.off(JaegerEvent.MOUSE_ENTER);
        ctx.barGroup.on(JaegerEvent.MOUSE_ENTER, () => {
            this.onMouseEnter(ctx);
        });
    }

    private setMouseLeaveListener(ctx: TLBar): void {
        ctx.barGroup.off(JaegerEvent.MOUSE_LEAVE);
        ctx.barGroup.on(JaegerEvent.MOUSE_LEAVE, () => {
            this.onMouseExit(ctx);
        });
    }
}
